import { takeLatest, call, put } from 'redux-saga/effects';
import {
  loadCustomers,
  loadCustomersSuccess,
  loadCustomersError,
  saveCustomerRequest,
  deleteCustomerRequest,
  collectBalanceRequest,
} from './actions';
import {
  listCustomers,
  createCustomer,
  updateCustomer,
  deleteCustomer,
  collectBalance,
} from '@/services/customers';
import { getActiveShopId } from '@/utils/shopSession';
import { hydrateCustomers } from '@/containers/posData/actions';
import { loadCredits, loadDueCollections } from '@/containers/Counter/actions';
import type { Customer } from '@/lib/types';
import type { CustomerInput } from '@/services/customers';

function mapCustomer(dto: any): Customer {
  return {
    id: String(dto.id),
    name: dto.name,
    phone: dto.phone,
    email: dto.email || undefined,
    address: dto.address || undefined,
    notes: dto.notes || undefined,
    customerType: (dto.customerType as Customer['customerType']) || 'retail',
    creditBalance: Number(dto.creditBalance ?? dto.credit_balance ?? 0) || 0,
    createdAt: dto.createdAt || new Date().toISOString(),
  };
}

function toInput(customer: Customer): CustomerInput {
  return {
    name: customer.name,
    phone: customer.phone,
    email: customer.email,
    address: customer.address,
    notes: customer.notes,
    customerType: customer.customerType,
  };
}

export function* handleLoadCustomers(): Generator<any, void, any> {
  try {
    const shopId = getActiveShopId();
    if (!shopId) {
      yield put(loadCustomersError('No active shop selected'));
      return;
    }
    const raw: any[] = yield call(listCustomers, shopId);
    const customers = raw.map(mapCustomer);
    yield put(hydrateCustomers(customers));
    yield put(loadCustomersSuccess(customers));
  } catch (e: any) {
    yield put(loadCustomersError(e?.message || 'Failed to load customers'));
  }
}

export function* handleSaveCustomer(
  action: ReturnType<typeof saveCustomerRequest>
): Generator<any, void, any> {
  try {
    const shopId = getActiveShopId();
    if (!shopId) return;
    const { customer, isNew } = action.payload;
    if (isNew) {
      yield call(createCustomer, shopId, toInput(customer));
    } else {
      yield call(updateCustomer, shopId, Number(customer.id), toInput(customer));
    }
    yield put(loadCustomers());
  } catch (e: any) {
    yield put(loadCustomersError(e?.message || 'Failed to save customer'));
  }
}

export function* handleDeleteCustomer(
  action: ReturnType<typeof deleteCustomerRequest>
): Generator<any, void, any> {
  try {
    const shopId = getActiveShopId();
    if (!shopId) return;
    yield call(deleteCustomer, shopId, Number(action.payload.id));
    yield put(loadCustomers());
  } catch (e: any) {
    yield put(loadCustomersError(e?.message || 'Failed to delete customer'));
  }
}

export function* handleCollectBalance(
  action: ReturnType<typeof collectBalanceRequest>
): Generator<any, void, any> {
  try {
    const shopId = getActiveShopId();
    if (!shopId) return;
    yield call(
      collectBalance,
      shopId,
      Number(action.payload.id),
      {
        amount: action.payload.amount,
        paymentMethod: action.payload.paymentMethod,
        note: action.payload.note,
      }
    );
    yield put(loadCustomers());
    yield put(loadCredits());
    yield put(loadDueCollections());
  } catch (e: any) {
    yield put(loadCustomersError(e?.message || 'Failed to record payment'));
  }
}

export default function* saga() {
  yield takeLatest(loadCustomers.type, handleLoadCustomers);
  yield takeLatest(saveCustomerRequest.type, handleSaveCustomer);
  yield takeLatest(deleteCustomerRequest.type, handleDeleteCustomer);
  yield takeLatest(collectBalanceRequest.type, handleCollectBalance);
}
